import { apiClient } from './api';
import type { LoginCredentials, RegisterCredentials, User } from '../types/auth';

export const authService = {
  /**
   * Log in with credentials.
   * POST /api/auth-token/ establishes an HttpOnly session cookie (no token in
   * the body), then GET /api/users/me/ returns the authenticated user.
   */
  async login(credentials: LoginCredentials): Promise<User> {
    await apiClient.post('/api/auth-token/', credentials);
    return apiClient.get<User>('/api/users/me/');
  },

  /**
   * Register a new user.
   * POST /api/users/register/ creates the account but does not establish a
   * session itself. AuthContext.register performs a follow-up login() to
   * authenticate the user (preserving the prior "registered = logged in"
   * behavior). The token/permissions fields in the response are stripped — the
   * SPA no longer uses them under session auth.
   */
  async register(credentials: RegisterCredentials): Promise<User> {
    const response = await apiClient.post<Record<string, any>>(
      '/api/users/register/',
      credentials,
    );
    // Drop server-only fields the SPA no longer persists (token is obsolete).
    const { token, permissions, ...userData } = response;
    return userData as User;
  },

  /**
   * Log out the current user.
   * POST /api/auth/logout/ clears the session server-side; local UI cache is
   * cleared regardless of the request outcome.
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout/');
    } catch (error) {
      console.warn('Logout request failed; clearing local state anyway:', error);
    }
    apiClient.clearToken();
  },

  /**
   * Fetch the current user from the server.
   * GET /api/users/me/ — authenticated via the session cookie.
   */
  async fetchCurrentUser(): Promise<User> {
    return apiClient.get<User>('/api/users/me/');
  },

  /**
   * Fetch a WSJ token for chat iframe authentication (separate from app login).
   * GET /api/users/wsj-token/
   * Returns: { token: string, expires_at: string }
   * Errors: 400 (not_wsj_user), 404 (no_session), 410 (session_expired), 503 (service_unavailable)
   */
  async fetchWsjToken(): Promise<{ token: string; expires_at: string }> {
    return apiClient.get<{ token: string; expires_at: string }>('/api/users/wsj-token/');
  },
};
